import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Players from "@/pages/Players";
import HotProps from "@/pages/HotProps";
import Injuries from "@/pages/Injuries";
import HRTracker from "@/pages/HRTracker";
import HRZone from "@/pages/HRZone";
import WnbaMatchups from "@/pages/WnbaMatchups";
import WnbaProjections from "@/pages/WnbaProjections";
import WnbaPlayers from "@/pages/WnbaPlayers";
import WnbaHotProps from "@/pages/WnbaHotProps";
import PropsPicks from "@/pages/PropsPicks";
import SportsBot from "@/pages/SportsBot";
import Matchups from "@/pages/Matchups";
import NflScores from "@/pages/NflScores";
import NflPlayers from "@/pages/NflPlayers";
import NflHotProps from "@/pages/NflHotProps";
import NflInjuries from "@/pages/NflInjuries";
import TDZone from "@/pages/TDZone";
import MyBets from "@/pages/MyBets";
import NhlMatchups from "@/pages/NhlMatchups";
import NhlHotProps from "@/pages/NhlHotProps";
import GoalsZone from "@/pages/GoalsZone";
import NhlPlayers from "@/pages/NhlPlayers";
import NbaMatchups from "@/pages/NbaMatchups";
import NbaProjections from "@/pages/NbaProjections";
import NbaPlayers from "@/pages/NbaPlayers";
import NbaHotProps from "@/pages/NbaHotProps";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext(null);
import React from 'react';

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, 
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          // Clear hash and navigate to dashboard with user data
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { state: { user: response.data } });
        } catch (error) {
          console.error('Auth error:', error);
          navigate('/');
        }
      } else {
        navigate('/');
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
      <div className="animate-pulse text-brand-cyan text-xl font-heading">
        Authenticating...
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/');
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="animate-pulse text-brand-cyan text-xl font-heading">
          Loading...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/players" element={
        <ProtectedRoute>
          <Players />
        </ProtectedRoute>
      } />
      <Route path="/hot-props" element={
        <ProtectedRoute>
          <HotProps />
        </ProtectedRoute>
      } />
      <Route path="/hr-tracker" element={
        <ProtectedRoute>
          <HRTracker />
        </ProtectedRoute>
      } />
      <Route path="/hr-zone" element={
        <ProtectedRoute>
          <HRZone />
        </ProtectedRoute>
      } />
      <Route path="/wnba/matchups" element={
        <ProtectedRoute>
          <WnbaMatchups />
        </ProtectedRoute>
      } />
      <Route path="/wnba/projections" element={
        <ProtectedRoute>
          <WnbaProjections />
        </ProtectedRoute>
      } />
      <Route path="/wnba/picks" element={
        <ProtectedRoute>
          <WnbaProjections />
        </ProtectedRoute>
      } />
      <Route path="/wnba/players" element={
        <ProtectedRoute>
          <WnbaPlayers />
        </ProtectedRoute>
      } />
      <Route path="/wnba/hot-props" element={
        <ProtectedRoute>
          <WnbaHotProps />
        </ProtectedRoute>
      } />
      <Route path="/props-picks" element={
        <ProtectedRoute>
          <PropsPicks />
        </ProtectedRoute>
      } />
      <Route path="/sports-bot" element={
        <ProtectedRoute>
          <SportsBot />
        </ProtectedRoute>
      } />
      <Route path="/my-bets" element={
        <ProtectedRoute>
          <MyBets />
        </ProtectedRoute>
      } />
      <Route path="/injuries" element={
        <ProtectedRoute>
          <Injuries />
        </ProtectedRoute>
      } />
      <Route path="/nfl/scores" element={
        <ProtectedRoute>
          <NflScores />
        </ProtectedRoute>
      } />
      <Route path="/nfl/players" element={
        <ProtectedRoute>
          <NflPlayers />
        </ProtectedRoute>
      } />
      <Route path="/nfl/hot-props" element={
        <ProtectedRoute>
          <NflHotProps />
        </ProtectedRoute>
      } />
      <Route path="/nfl/td-zone" element={
        <ProtectedRoute>
          <TDZone />
        </ProtectedRoute>
      } />
      <Route path="/nfl/injuries" element={
        <ProtectedRoute>
          <NflInjuries />
        </ProtectedRoute>
      } />
      <Route path="/nhl/matchups" element={
        <ProtectedRoute>
          <NhlMatchups />
        </ProtectedRoute>
      } />
      <Route path="/nhl/hot-props" element={
        <ProtectedRoute>
          <NhlHotProps />
        </ProtectedRoute>
      } />
      <Route path="/nhl/goals-zone" element={
        <ProtectedRoute>
          <GoalsZone />
        </ProtectedRoute>
      } />
      <Route path="/nhl/players" element={
        <ProtectedRoute>
          <NhlPlayers />
        </ProtectedRoute>
      } />
      <Route path="/nba/matchups" element={
        <ProtectedRoute>
          <NbaMatchups />
        </ProtectedRoute>
      } />
      <Route path="/nba/projections" element={
        <ProtectedRoute>
          <NbaProjections />
        </ProtectedRoute>
      } />
      <Route path="/nba/players" element={
        <ProtectedRoute>
          <NbaPlayers />
        </ProtectedRoute>
      } />
      <Route path="/nba/hot-props" element={
        <ProtectedRoute>
          <NbaHotProps />
        </ProtectedRoute>
      } />
      <Route path="/matchups" element={
        <ProtectedRoute>
          <Matchups />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#0a1628]">
      <BrowserRouter>
        <AppRouter />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
